import axios from 'axios';
import { BASE_URL, attachClientId } from 'Auth';
// Relativo, não `@/`: o jest dos testes unitários do SPA não conhece o alias do rspack.
import { sessionIdOf, handleSessionExpired } from './helpers/session-expired';

const api = axios.create({
	baseURL: BASE_URL
});

// `axios.create` não herda os interceptors do axios global. A sessão vai no cookie HttpOnly, que o
// navegador manda sozinho; o `attachClientId` põe o identificador do navegador (a sessão é amarrada
// a ele), o `withCredentials` e o carimbo da sessão.
api.interceptors.request.use(attachClientId);

api.interceptors.request.use(request => {
	// Envia o fuso horário do navegador para o backend formatar datas (ex: carimbo de assinatura digital)
	try {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (timeZone) {
			request.headers['X-Timezone'] = timeZone;
		}
	} catch (e) {
		// Intl indisponível — backend usa o fuso padrão
	}

	// Se for FormData, não adicionar Content-Type automático
	if (request.data instanceof FormData) {
		delete request.headers['Content-Type'];
	}

	return request;
});

window.addEventListener('unhandledrejection', error => {
	console.log('Console log do unhandledrejection', error);
	console.log('Data do unhandledrejection');
});

api.interceptors.response.use(
	response => {
		// Se for Blob (arquivo), passa direto
		if (response.data instanceof Blob) {
			return Promise.resolve(response);
		}
		if (response.data.status === 'success') {
			return Promise.resolve(response.data);
		} else {
			return Promise.reject(response.data);
		}
	},
	error => {
		// Só é sessão expirada se a chamada saiu com uma sessão (o carimbo do `attachClientId`). A
		// teleconsulta do paciente chama sem login (vale o `access_token` do link): tratar o 401 dela
		// como expiração recarregava a página em loop, com o mesmo link ruim.
		if (error && error.response && error.response.status === 401 && sessionIdOf(error.config)) {
			handleSessionExpired(sessionIdOf(error.config));
		} else {
			return Promise.reject(error);
		}
	}
);

/*
 * Prazo do PUT por falta de progresso, não pelo total: um prazo fixo cortava trechos de vídeo em rede
 * lenta (4,5 MB a 0,3 Mbps passam de 2 minutos, e 3 tentativas iguais falhavam igual — corte
 * permanente), enquanto uma conexão meio aberta, sem prazo nenhum, segurava a tela em "Salvando
 * gravação…" por minutos. Assim só cai o PUT que ficou 60 s sem enviar nem receber nada.
 */
const PUT_IDLE_TIMEOUT_MS = 60000;

// PUT direto no S3 por URL pré-assinada: fora do `api` (axios) de propósito — o header de autenticação
// e a baseURL do interceptor invalidariam a assinatura da URL. XHR porque o `fetch` não informa o
// progresso do envio.
function putToPresignedUrl(url, body) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		let idleTimer = null;
		const settle = callback => value => {
			clearTimeout(idleTimer);
			callback(value);
		};
		const armIdleTimer = () => {
			clearTimeout(idleTimer);
			idleTimer = setTimeout(() => xhr.abort(), PUT_IDLE_TIMEOUT_MS);
		};

		xhr.open('PUT', url);
		xhr.upload.onprogress = armIdleTimer;
		xhr.onprogress = armIdleTimer;
		xhr.onload = settle(() => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status }));
		xhr.onerror = settle(() => reject(new Error('[Api]: falha de rede no envio para o S3')));
		xhr.onabort = settle(() => reject(new Error('[Api]: envio para o S3 parado por 60 s')));
		armIdleTimer();
		xhr.send(body);
	});
}

const apis = {
	getUserInfo: data => api.get('api/user/info', { params: data }),
	getUserLimitInfo: () => api.get('api/user/limit-info'),
	getCouncils: () => api.get('api/conselhos'),
	listMemedSpecializations: () => api.get('api/memed/especialidades'),
	uploadUserPhoto: data => api.post('api/upload/user-photo', data),
	uploadUserSignature: data => api.post('api/upload/user-signature', data),
	uploadPatientXLS: data => api.post('api/upload/patient-xls', data),
	uploadAccountPhoto: data => api.post('api/upload/account-photo', data),
	uploadPatientPhoto: data => api.post('api/upload/patient-photo', data),
	uploadPatientPhotoBase64: data => api.post('api/upload/patient-photo-base64', data),
	uploadHealthRecordFile: data => api.post('api/upload/health-record-file', data),
	uploadHealthRecordFileBase64: data => api.post('api/upload/health-record-file-base64', data),
	convertHeicToJpeg: data => api.post('api/convert/heic-to-jpeg', data, { responseType: 'blob' }),

	getAccount: () => api.get('api/account'),
	updateAccount: data => api.put('api/account', data),
	getAccountLastAppointments: params => api.get('api/accounts/last-appointments', { params }),
	getAccountPrintSettings: () => api.get('api/account/print-settings'),
	updateAccountPrintSettings: data => api.put('api/account/print-settings', data),
	previewAccountPrintSettings: data => api.post('api/account/print-settings/preview', data),
	getLaudoPrintSettings: variant => api.get('api/account/laudo-print-settings', { params: { variant } }),
	updateLaudoPrintSettings: data => api.put('api/account/laudo-print-settings', data),
	previewLaudoPrintSettings: data => api.post('api/account/laudo-print-settings/preview', data),

	getSubscriptions: () => api.get('api/subscription'),
	getSubscriptionPlans: () => api.get('api/subscription/plans'),
	getChargeInfo: data => api.get('api/subscription/charge-info', { params: data }),
	createSubscription: data => api.post('api/subscription', data),
	cancelSubscription: () => api.delete('api/subscription'),

	getUser: data => api.get('api/user/' + data.id),
	listUsers: data => api.get('api/users', { params: data }),
	addUser: data => api.post('api/user', data),
	updateUser: data => api.put('api/user/' + data.id, data),
	updateAppointmentAvailability: data => api.put('api/user/appointments/' + data.id, data),
	toggleActiveUser: data => api.put('api/user/disable/' + data.id, data),
	changeUserPassword: data => api.put('api/user/password', data),
	getIcalendarToken: () => api.get('api/my-agenda-integration/token'),
	// A tabela de comparação do Resultado de exames, montada pela API (a mesma do app).
	getExamComparison: (patientId, templateFormId) => api.get('api/exam-comparison', { params: { patient_id: patientId, template_form_id: templateFormId } }),
	// O link iCal e o fuso em que o feed lê a agenda (ex.: Europe/Lisbon numa clínica em Portugal).
	getCalendarSettings: () => api.get('api/my-agenda-integration/settings'),
	updateCalendarTimezone: timezone => api.put('api/my-agenda-integration/settings', { timezone }),
	rotateIcalendarToken: data => api.post('api/my-agenda-integration/token/rotate', data),

	get2faSettings: () => api.get('api/auth/2fa/settings'),
	update2faSettings: data => api.put('api/auth/2fa/settings', data),
	listSessions: () => api.get('api/auth/sessions'),
	revokeSession: data => api.delete(`api/auth/sessions/${data.id}`),
	revokeOtherSessions: () => api.post('api/auth/sessions/revoke-others'),

	requestContactChange: data => api.post('api/user/contact-change/request', data),
	resendContactChangeCode: data => api.post('api/user/contact-change/resend', data),
	confirmContactChange: data => api.post('api/user/contact-change/confirm', data),

	listDoctors: data => api.get('api/doctors', { params: data }),

	listPatients: data => api.get('api/patients', { params: data }),
	/** Listagem da tela de Pacientes: traz convênio padrão e último/próximo atendimento. */
	listPatientsForManagement: params => api.get('api/patients/list', { params }),
	pdfPatientRecordSearch: params => api.get('api/patients/list/export/pdf', { params, responseType: 'blob' }),
	expandPatientRecordTerms: terms => api.post('api/patients/list/record-terms/expand', { terms }),
	addPatient: data => api.post('api/patient', data),
	getPatient: data => api.get('api/patient/' + data.id, data),
	searchPatient: data => api.get('api/patient/search', { params: data }),
	getPatientMergeSummary: data => api.get('api/patients/merge/summary', { params: data }),
	mergePatients: data => api.post('api/patients/merge', data),
	// Página Manutenção (`/well-of-urd`), só para o usuário DEFAULT.
	getAccountMaintenanceExpiration: () => api.get('api/account-maintenance/expiration'),
	updateAccountMaintenanceExpiration: data => api.put('api/account-maintenance/expiration', data),
	listAccountMaintenanceLogs: params => api.get('api/account-maintenance/logs', { params }),
	// Devolve o link de uso único; o download em si é aberto pelo navegador (ver useAccountMaintenance).
	createAccountMaintenanceExport: data => api.post('api/account-maintenance/exports', data),
	getSpecialActionLog: params => api.get('api/special-action-logs', { params }),
	undoSpecialAction: id => api.post(`api/special-action-logs/${id}/undo`),
	getPatientAppointments: patientId => api.get(`api/patients/${patientId}/next-and-upcoming-appointments`),
	getAppointmentEvents: appointmentId => api.get(`api/appointment/${appointmentId}/events`),
	listAppointmentHealthRecords: appointmentId => api.get(`api/appointment/${appointmentId}/health-records`),
	getAppointmentReturnEligibility: (patientId, referenceDate) => api.get(`api/patients/${patientId}/return-eligibility`, { params: referenceDate ? { reference_date: referenceDate } : undefined }),
	checkPatientCpf: data => api.get('api/patients/check-cpf', { params: data }),
	updatePatient: data => api.put('api/patient/' + data.id, data),
	updatePatientPhoto: data => api.put('api/patient/photo', data),
	updatePatientPhone: data => api.put('api/patient/phone', data),
	// Só o campo VIP: o PUT api/patient/:id regrava etiquetas e convênios a partir do corpo.
	updatePatientVip: data => api.put('api/patients/' + data.id + '/vip', { is_vip: data.is_vip }),
	sendWhatsapp: data => api.post('api/patient/whatsapp', data),
	removePatient: data => api.delete('api/patient/' + data.id, { data: data }),
	getCities: () => api.get('api/patient/cities'),
	listPatientTags: () => api.get('api/patient/tags'),
	listPatientTagsByPatient: patientId => api.get(`api/patient/${patientId}/tags-list`),
	addPatientTagItem: (patientId, data) => api.post(`api/patient/${patientId}/tags-item`, data),
	removePatientTagItem: (patientId, tagId) => api.delete(`api/patient/${patientId}/tags-item/${tagId}`),

	listPatientCids: patientId => api.get(`api/patient/${patientId}/cids`),
	addPatientCid: (patientId, data) => api.post(`api/patient/${patientId}/cids`, data),
	removePatientCid: (patientId, cidId) => api.delete(`api/patient/${patientId}/cids/${cidId}`),
	searchCids: query => api.get('api/cids/search', { params: { q: query } }),

	listPatientNotes: data => api.get('api/patient-notes', { params: data }),
	addPatientNote: data => api.post('api/patient-note', data),
	getPatientNote: data => api.get('api/patient-note/' + data.id, data),
	updatePatientNote: data => api.put('api/patient-note/' + data.id, data),
	removePatientNote: data => api.delete('api/patient-note/' + data.id, { data: data }),

	listHofPatientPlans: data => api.get('api/hof-patient-plans', { params: data }),
	addHofPatientPlan: data => api.post('api/hof-patient-plan', data),
	getHofPatientPlan: data => api.get('api/hof-patient-plan/' + data.id, { params: data }),
	updateHofPatientPlan: data => api.put('api/hof-patient-plan/' + data.id, data),
	removeHofPatientPlan: data => api.delete('api/hof-patient-plan/' + data.id, { data: data }),
	checkHofPatientPlanStock: data => api.post('api/hof-patient-plan/check-stock', data),

	listReportHofPatientPlans: data => api.get('api/reports/hof-patient-plans', { params: data }),
	pdfReportHofPatientPlans: data => api.get('api/reports/hof-patient-plans/pdf', { params: data }),
	xlsReportHofPatientPlans: data => api.get('api/reports/hof-patient-plans/xls', { params: data }),

	listReportBudget: data => api.get('api/reports/budget', { params: data }),
	pdfReportBudget: data => api.get('api/reports/budget/pdf', { params: data }),
	xlsReportBudget: data => api.get('api/reports/budget/xls', { params: data }),

	listReportTransfers: data => api.get('api/reports/transfers', { params: data }),
	pdfReportTransfers: data => api.get('api/reports/transfers/pdf', { params: data }),
	xlsReportTransfers: data => api.get('api/reports/transfers/xls', { params: data }),

	listReportPatientProcedureStatus: data => api.get('api/reports/patient-procedure-status', { params: data }),
	pdfReportPatientProcedureStatus: data => api.get('api/reports/patient-procedure-status/pdf', { params: data }),
	xlsReportPatientProcedureStatus: data => api.get('api/reports/patient-procedure-status/xls', { params: data }),
	listReportAppointmentProcedureStatus: data => api.get('api/reports/appointment-procedure-status', { params: data }),
	pdfReportAppointmentProcedureStatus: data => api.get('api/reports/appointment-procedure-status/pdf', { params: data }),
	xlsReportAppointmentProcedureStatus: data => api.get('api/reports/appointment-procedure-status/xls', { params: data }),
	listReportBudgetV2ProcedureStatus: data => api.get('api/reports/budget-v2-procedure-status', { params: data }),
	pdfReportBudgetV2ProcedureStatus: data => api.get('api/reports/budget-v2-procedure-status/pdf', { params: data }),
	xlsReportBudgetV2ProcedureStatus: data => api.get('api/reports/budget-v2-procedure-status/xls', { params: data }),

	/**
	 * @deprecated Sem consumidor desde a remoção da tabela "Procedimentos realizados"
	 * da aba de HOF. As execuções são criadas, revertidas e listadas pelo
	 * planejamento: `updateHofPatientPlan` envia `patientPlanProcedures` com
	 * `toAdd`/`done_deleted`, e `getHofPatientPlan` devolve `executions` por
	 * procedimento. Não usar em tela nova.
	 */
	addHofPatientProcedure: data => api.post('api/hof-patient-procedure', data),
	/** @deprecated Ver addHofPatientProcedure. */
	listHofPatientProcedures: data => api.get('api/hof-patient-procedures', { params: data }),
	/** @deprecated Ver addHofPatientProcedure. */
	removeHofPatientProcedure: data => api.delete('api/hof-patient-procedure/' + data.id, { data: data }),

	addHofPatientProduct: data => api.post('api/hof-patient-product', data),
	listHofPatientProducts: data => api.get('api/hof-patient-products', { params: data }),
	removeHofPatientProduct: data => api.delete('api/hof-patient-product/' + data.id, { data: data }),

	listAgenda: data => api.get('api/agenda/appointments', { params: data }),
	listAppointments: data => api.get('api/appointments', { params: data }),
	addAppointment: data => api.post('api/appointment', data),
	getAppointment: data => api.get('api/appointment/' + data.id, data),
	listAppointmentProcedures: data => api.get('api/appointment/' + data.id + '/procedures'),
	updateAppointment: data => api.put('api/appointment/' + data.id, data),
	removeAppointment: data => api.delete('api/appointment/' + data.id, { data: data }),
	removeAppointmentGroup: data => api.delete('api/appointment/' + data.id, { params: { update_scope: 'group', group_uuid: data.group_uuid } }),
	changeAppointmentDate: data => api.put('api/appointment/date', data),
	changeAppointmentStatus: data => api.put('api/appointment/status', data),
	listAppointmentFiles: data => api.get('api/appointment/files', { params: data }),
	addAppointmentFile: data => api.post('api/appointment/file', data),
	removeAppointmentFile: id => api.delete('api/appointment/file/' + id),
	uploadProcedureAttachment: data => api.post('api/procedure-attachment', data),
	removeProcedureAttachment: appointmentProcedureId => api.delete('api/procedure-attachment/' + appointmentProcedureId),
	listProcedureAttachments: data => api.get('api/procedure-attachment', { params: data }),
	getProcedureAttachmentForHealthRecord: recordId => api.get('api/procedure-attachment/for-health-record/' + recordId),
	listPriceTables: data => api.get('api/price-tables', { params: data }),
	createPriceTable: data => api.post('api/price-tables', data),
	updatePriceTable: (id, data) => api.put('api/price-tables/' + id, data),
	removePriceTable: id => api.delete('api/price-tables/' + id),
	listProcedurePriceTables: data => api.get('api/procedure-price-tables', { params: data }),
	getProcedurePriceTable: id => api.get('api/procedure-price-tables/' + id),
	createProcedurePriceTable: data => api.post('api/procedure-price-tables', data),
	updateProcedurePriceTable: (id, data) => api.put('api/procedure-price-tables/' + id, data),
	removeProcedurePriceTable: id => api.delete('api/procedure-price-tables/' + id),
	startHealthRecordVideo: data => api.post('api/health-record-video/start', data),
	getHealthRecordVideoUploadUrls: (id, data) => api.post(`api/health-record-video/${id}/urls`, data),
	finishHealthRecordVideo: id => api.post(`api/health-record-video/${id}/finish`),
	putToPresignedUrl,
	abortHealthRecordVideo: id => api.post(`api/health-record-video/${id}/abort`),
	getOpenHealthRecordVideo: params => api.get('api/health-record-video/open', { params }),
	downloadHealthRecordVideo: id => api.get(`api/health-record-video/${id}/download`, { responseType: 'blob' }),
	startHealthRecordVideoAttachment: data => api.post('api/health-record-video-attachment/start', data),
	finishHealthRecordVideoAttachment: id => api.post(`api/health-record-video-attachment/${id}/finish`),
	abortHealthRecordVideoAttachment: id => api.post(`api/health-record-video-attachment/${id}/abort`),
	linkFilesToReports: data => api.post('api/health-record-report-files/link', data),
	generateQRCodeAnamnese: data => api.post('api/appointment/anamnese-qrcode', data),
	listSentAnamneses: data => api.get(`api/appointment/${data.appointment_id}/sent-anamneses`, { params: { patient_id: data.patient_id } }),
	deleteSentAnamnese: data => api.delete(`api/appointment/sent-anamnese/${data.sent_anamnese_id}`),
	resendSentAnamneseWhatsapp: data => api.post(`api/appointment/sent-anamnese/${data.sent_anamnese_id}/resend-whatsapp`),
	generateReceipt: data => api.post('api/appointment/generate-receipt', data),
	generateCreditReceipt: data => api.post('api/appointment/generate-credit-receipt', data),
	generateAttendanceSheet: data => api.post('api/attendance-sheet', data),
	generateWristband: data => api.post('api/wristband', data),
	generatePatientLabel: data => api.post('api/patient-label', data),
	listUnfinishedAppointment: () => api.get('api/appointment/unfinished-appointment'),

	addAppointmentBlock: data => api.post('api/appointment-block', data),
	addAppointmentBlockBulk: data => api.post('api/appointment-block/bulk', data),
	getAppointmentBlock: data => api.get('api/appointment-block/' + data.id, data),
	updateAppointmentBlock: data => api.put('api/appointment-block/' + data.id, data),
	removeAppointmentBlock: data => api.delete('api/appointment-block/' + data.id, { data: data }),
	removeAppointmentBlockGroup: data => api.delete('api/appointment-block/' + data.id + '/group', { data: data }),
	changeAppointmentBlockDate: data => api.put('api/appointment-block/date', data),

	listReportAppointments: data => api.get('api/report/appointments', { params: data }),
	pdfReportAppointments: data => api.get('api/report/appointments/pdf', { params: data }),
	xlsReportAppointments: data => api.get('api/report/appointments/xls', { params: data }),

	listHealthRecords: data => api.get('api/health-records', { params: data }),
	listHealthRecordsReport: data => api.get('api/health-records/report', { params: data }),
	exportLaudosReportPdf: data => api.get('api/health-records/report/pdf', { params: data }),
	exportLaudosReportXls: data => api.get('api/health-records/report/xls', { params: data }),
	healthRecordUpdateStatus: (id, data) => api.patch(`api/health-record/${id}/status`, data),
	getOverviewHealthRecords: data => api.get('api/health-records/overview', { params: data }),
	listHealthRecordFiles: data => api.get('api/health-records/files', { params: data }),
	addHealthRecord: data => api.post('api/health-record', data),
	addComment: data => api.post('api/health-record/comment', data),
	updateComment: data => api.put('api/health-record/comment/' + data.id, data),
	getHealthRecord: data => api.get('api/health-record/' + data.id),
	pdfHealthRecord: data => api.get('api/health-record/pdf', { params: data }),
	pdfLaudoHealthRecord: data => api.get('api/health-record/laudo-pdf', { params: data }),
	shareHealthRecordByEmail: data => api.post('api/health-record/share-email', data),
	// Impressão do prontuário completo: uma chamada, um ZIP (prontuario.pdf + pasta anexos/).
	// Vem como blob (application/zip), não JSON — o interceptor deixa blob passar direto.
	getHealthRecordFullPrint: (params, onDownloadProgress) => api.get('api/health-records/full-print', { params, responseType: 'blob', onDownloadProgress }),
	getExternalPdfLink: data => api.get('api/health-record/pdf/link', { params: data }),
	guidePdf: data => api.get('api/finance/guide/pdf', { params: data }),
	shareFinanceGuideViaWhatsapp: data => api.post('api/finance/guide/share-whatsapp', data),
	getTissGuide: data => api.get('api/tiss/guide', { params: data }),
	createTissGuide: data => api.post('api/tiss/guide', data),
	updateTissGuide: data => api.put('api/tiss/guide/' + data.id, data),
	updateHealthRecord: data => api.put('api/health-record/' + data.id, data),
	listTussExameImagemTipos: () => api.get('api/tuss-exames-imagem/tipos'),
	listTussExameImagemPartesCorpo: params => api.get('api/tuss-exames-imagem/partes-corpo', { params }),
	listTussExameImagemProcedimentos: params => api.get('api/tuss-exames-imagem/procedimentos', { params }),
	listHealthRecordPrescriptionProcedures: recordId => api.get('api/health-record-prescription-procedures/' + recordId),
	saveHealthRecordPrescriptionProcedures: (recordId, data) => api.put('api/health-record-prescription-procedures/' + recordId, data),
	removeHealthRecord: data => api.delete('api/health-record/' + data.id, { data: data }),
	removeHealthRecordFile: data => api.delete('api/health-record/file', { data: data }),
	removeHealthRecordFileById: data => api.delete('api/health-record/file/' + data.id),
	removeHealthRecordMemed: data => api.delete('api/health-record/memed/' + data.id, { data: data }),
	removeComment: data => api.delete('api/health-record/comment', { data: data }),
	listComments: data => api.get('api/health-records/comments', { params: data }),
	listGalleryImages: (patientId, data) => api.get(`api/patients/${patientId}/gallery-images`, { params: data }),
	setGalleryImageTag: (id, data) => api.put(`api/gallery-images/${id}/tag`, data),
	composeGalleryImages: (patientId, data) => api.post(`api/patients/${patientId}/gallery-images/compose`, data),
	openAiChat: (context, message) => api.post('api/ai/chat', message ? { context, message } : { context }),
	askAiChat: (threadId, message, context) => api.post(`api/ai/chat/${encodeURIComponent(threadId)}/messages`, { message, context }),
	sendGalleryImageWhatsapp: id => api.post(`api/gallery-images/${id}/send-whatsapp`),
	listImageTags: data => api.get('api/image-tags', { params: data }),
	createImageTag: data => api.post('api/image-tags', data),
	togglePinHealthRecord: data => api.post('api/health-record/pinned', data),
	togglePinHealthRecordFile: data => api.post('api/health-record/file/pinned', data),
	getMergedFile: data => api.get('api/health-records/merge-files', { params: data }),
	getHealthRecordsTabs: () => api.get('api/health-record-template/tabs'),
	listHealthRecordTemplates: data => api.get('api/health-record-templates', { params: data }),
	addHealthRecordTemplate: data => api.post('api/health-record-template', data),
	getHealthRecordTemplate: data => api.get('api/health-record-template/' + data.id),
	updateHealthRecordTemplate: data => api.put('api/health-record-template/' + data.id, data),
	toggleActiveHealthRecordTemplate: data => api.put('api/health-record-template/disable/' + data.id, data),
	getPrenatalState: data => api.get('api/health-record/prenatal-state', { params: data }),
	createHealthRecordProcedureEntry: data => api.post('api/health-record/procedure', data),
	createManualHealthRecordReport: data => api.post('api/health-record-report', data),

	listCid11: params => api.get('api/cid11', { params }),

	listHealthRecordPrintableTemplates: data => api.get('api/health-record-printable-templates', { params: data }),
	addHealthRecordPrintableTemplate: data => api.post('api/health-record-printable-template', data),
	getHealthRecordPrintableTemplate: data => api.get('api/health-record-printable-template/' + data.id),
	updateHealthRecordPrintableTemplate: data => api.put('api/health-record-printable-template/' + data.id, data),
	toggleActiveHealthRecordPrintableTemplate: data => api.put('api/health-record-printable-template/disable/' + data.id, data),

	getHealthRecordSettings: () => api.get('api/health-record-settings'),
	updateHealthRecordSettings: data => api.post('api/health-record-settings', data),
	resetHealthRecordSettings: () => api.post('api/health-record-settings/reset'),
	getUserHealthRecordPreferences: () => api.get('api/user-health-record-preferences'),
	saveUserHealthRecordPreferences: data => api.put('api/user-health-record-preferences', data),

	listContractTemplates: data => api.get('api/contract-templates', { params: data }),
	addContractTemplate: data => api.post('api/contract-template', data),
	getContractTemplate: data => api.get('api/contract-template/' + data.id, data),
	updateContractTemplate: data => api.put('api/contract-template/' + data.id, data),
	toggleActiveContractTemplate: data => api.put('api/contract-template/disable/' + data.id, data),

	listContracts: data => api.get('api/contracts', { params: data }),
	addContract: data => api.post('api/contract', data),
	addContractFile: data => api.post('api/contract/file', data),
	pdfContract: data => api.get('api/contract/pdf', { params: data }),
	getContract: data => api.get('api/contract/' + data.id, data),
	updateContractSigned: data => api.put('api/contract/signed', data),
	removeContract: data => api.delete('api/contract/' + data.id, { data: data }),
	sendToSign: data => api.get('/api/sign/send-sign-link', { params: data }),
	resendSignNotification: data => api.post('/api/sign/resend-notification', data),
	generateSignatedContract: data => api.get('/api/sign/generate-signed-contract/' + data.contract_id, { params: data }),
	removeSignature: data => api.delete('/api/sign/remove-signature/' + data.signatureId),

	getAgendaSettings: data => api.get('api/agenda/settings', { params: data }),
	updateAgendaSettings: data => api.put('api/agenda/settings', data),

	// Tipos de atendimento exibidos no autoagendamento. O contrato é pelos OCULTOS
	// (ver shared/schemas/self-scheduling-settings) — a inversão vive na tela.
	getSelfSchedulingTemplates: () => api.get('api/agenda/self-scheduling-templates'),
	updateSelfSchedulingTemplates: data => api.put('api/agenda/self-scheduling-templates', data),

	listAgendaSettingsAvailabilities: data => api.get('api/agenda/availabilies', { params: data }),
	addAgendaSettingsAvailability: data => api.post('api/agenda/availability', data),
	updateAgendaSettingsAvailability: data => api.put('api/agenda/availability/' + data.id, data),
	removeAgendaSettingsAvailability: data => api.delete('api/agenda/availability/' + data.id, { data: data }),

	listTemplatesUsers: data => api.get('api/agenda/templates-users', { params: data }),
	addTemplatesUsers: data => api.post('api/agenda/templates-users', data),
	updateTemplatesUsers: data => api.put('api/agenda/templates-users/' + data.id, data),
	removeTemplatesUsers: data => api.delete('api/agenda/templates-users/' + data.id, { data: data }),

	addAppointmentTemplate: data => api.post('api/agenda/template', data),
	listAgendaColors: data => api.get('api/agenda/colors', { params: data }),
	listAppointmentTemplates: data => api.get('api/agenda/templates', { params: data }),
	getAppointmentTemplate: data => api.get('api/agenda/template/' + data.id),
	updateAppointmentTemplate: data => api.put('api/agenda/template/' + data.id, data),
	removeAppointmentTemplate: data => api.delete('api/agenda/template/' + data.id, { data: data }),
	listHolidays: data => api.get('api/agenda/holidays', { params: data }),
	addHoliday: data => api.post('api/agenda/holidays', data),
	updateHoliday: data => api.put('api/agenda/holidays/' + data.id, data),
	removeHoliday: data => api.delete('api/agenda/holidays/' + data.id),
	listAppointmentToAttendToday: data => api.get('api/appointment/to-attend-today', { params: data }),
	startStopAttendance: data => api.patch('api/appointment/start-stop-attendance', data),

	listFinances: data => api.get('api/finances', { params: data }),
	addFinance: data => api.post('api/finance', data),
	updateFinance: data => api.put('api/finance/' + data.id, data),
	removeFinance: data => api.delete('api/finance/' + data.id),
	pdfFinances: data => api.get('api/finance/pdf', { params: data }),
	xlsFinances: data => api.get('api/finance/xls', { params: data }),
	pdfCashFlow: data => api.get('api/finance/cashflow/pdf', { params: data }),
	xlsCashFlow: data => api.get('api/finance/cashflow/xls', { params: data }),
	// MF-1143: exportações assíncronas (o painel de exportações acompanha e baixa; ver ReportExportsContext)
	createReportExport: data => api.post('api/report-exports', data),
	listReportExports: () => api.get('api/report-exports'),
	cancelReportExport: id => api.post(`api/report-exports/${id}/cancel`),
	retryReportExport: id => api.post(`api/report-exports/${id}/retry`),
	downloadReportExport: id => api.post(`api/report-exports/${id}/download`),
	dismissReportExport: id => api.post(`api/report-exports/${id}/dismiss`),
	addFinanceFile: data => api.post('api/finance/file', data),
	getCashFlow: data => api.get('api/finance/cashflow', { params: data }),

	// Fechamento de Caixa — relatório (JSON) e exportações (arquivo em memória, stream binário)
	getCashClosing: data => api.get('api/cash-closing', { params: data }),
	pdfCashClosing: data => api.get('api/cash-closing/export/pdf', { params: data, responseType: 'blob' }),
	xlsCashClosing: data => api.get('api/cash-closing/export/xlsx', { params: data, responseType: 'blob' }),

	getNotificationMessagesTemplate: data => api.get('api/notifications/templates', { params: data }),
	createCustomNotificationTemplate: data => api.post('api/notifications/templates', data),
	updateCustomNotificationTemplateStatus: data => api.put('api/notifications/templates/status', data),

	getNotifications: data => api.get('api/notifications', { params: data }),

	listSuppliers: data => api.get('api/suppliers', { params: data }),
	addSupplier: data => api.post('api/supplier', data),
	updateSupplier: data => api.put('api/supplier/' + data.id, data),
	toggleActiveSupplier: data => api.put('api/supplier/status/' + data.id, data),
	searchSupplier: data => api.get('api/supplier/search', { params: data }),

	listFinanceProvisions: data => api.get('api/finance/provisions', { params: data }),
	getFinanceProvision: data => api.get('api/finance/provision/' + data.id, data),
	addFinanceProvision: data => api.post('api/finance/provision', data),
	// updateFinanceProvision: (data) => api.put('api/finance/provision/' + data.id, data), // TODO validar Follow Pay antes de usar
	removeFinanceProvision: data => api.delete('api/finance/provision/' + data.id),
	pdfFinanceProvisions: data => api.get('api/finance/provisions/pdf', { params: data }),
	xlsFinanceProvisions: data => api.get('api/finance/provisions/xls', { params: data }),
	addFinanceProvisionFile: data => api.post('api/finance/provision/file', data),
	splitFinanceProvision: data => api.put('api/finance/provision/split/' + data.id, data),
	editFinanceProvisionGroup: data => api.put('/api/finance/provision/group/' + data.id, data),
	editFinanceProvisionInstallment: data => api.put('/api/finance-provision-installments/' + data.id, data),
	getPatientBalance: data => api.get('api/finance/provisions/balance', { params: data }),
	getAllPatientsBalance: data => api.get('api/finance/provisions/patients-balance', { params: data }),
	pdfAllPatientsBalance: data => api.get('api/finance/provisions/patients-balance-pdf', { params: data }),
	xlsAllPatientsBalance: data => api.get('api/finance/provisions/patients-balance-xls', { params: data }),

	listTickets: data => api.get('api/tickets', { params: data }),
	updateTicket: data => api.put('api/tickets', data),
	readTicket: data => api.put('api/tickets/read', data),
	closeTicket: data => api.delete('api/ticket/' + data.id),

	listHofPositions: data => api.get('api/settings-hof/positions', { params: data }),

	addAlertReturn: data => api.post('api/alert-return', data),
	listAlertReturns: () => api.get('api/alert-return'),
	updateAlertReturn: (id, data) => api.put('api/alert-return/' + id, data),
	removeAlertReturn: id => api.delete('api/alert-return/' + id),

	addProcedure: data => api.post('api/procedure', data),
	listProcedures: data => api.get('api/procedures', { params: data }),
	searchTuss: params => api.get('api/tuss/search', { params }),
	searchTussProcedures: params => api.get('api/tuss/procedures', { params }),
	searchAmb: params => api.get('api/amb/search', { params }),
	searchProcedures: params => api.get('api/procedures/search', { params }),
	searchProceduresForAppointment: params => api.get('api/procedures/for-appointment', { params }),
	getProcedure: id => api.get('api/procedure/' + id),
	updateProcedure: data => api.put('api/procedure/' + data.id, data),
	removeProcedure: data => api.delete('api/procedure', { data: data }),
	toggleActiveProcedure: data => api.put('api/procedure/status', data),
	getProcedurePlanPrices: id => api.get(`api/procedure/${id}/plans`),

	addBudget: data => api.post('api/budget', data),
	pdfBudget: data => api.get('api/budget/pdf', { params: data }),
	listBudgets: data => api.get('api/budgets', { params: data }),
	updateBudget: data => api.put('api/budget/' + data.id, data),
	removeBudget: data => api.delete('api/budget', { data: data }),
	updateBudgetStatus: data => api.put('api/budget/status', data),

	//budgetV2
	listBudgetsV2: data => api.get('api/budgets-v2', { params: data }),
	listBudgetSessionHistory: patientId => api.get(`api/patients/${patientId}/budget-session-history`),
	getBudgetV2: data => api.get('api/budgets-v2/' + data.id),
	createBudgetV2: data => api.post('api/budgets-v2', data),
	updateBudgetV2: data => api.put('api/budgets-v2/' + data.id, data),
	deleteBudgetV2: data => api.delete('api/budgets-v2/' + data.id),
	listBudgetTemplatesV2: params => api.get('api/budgets-v2/templates', { params }),
	getBudgetTemplateV2: templateId => api.get(`api/budgets-v2/templates/${templateId}`),
	createBudgetTemplateV2: data => api.post('api/budgets-v2/templates', data),
	updateBudgetTemplateV2: data => api.put('api/budgets-v2/templates/' + data.id, data),
	deleteBudgetTemplateV2: id => api.delete('api/budgets-v2/templates/' + id),
	listBudgetRequestersV2: params => api.get('api/budgets-v2/requesters', { params }),
	uploadBudgetImageV2: (budgetId, formData) => api.post(`api/budgets-v2/${budgetId}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
	removeBudgetImageV2: (budgetId, imageId) => api.delete(`api/budgets-v2/${budgetId}/images/${imageId}`),
	bulkApproveBudgetProceduresV2: data => api.put('api/budgets-v2/' + data.budgetId + '/bulk-approval', { approved_procedure_ids: data.approvedProcedureIds }),
	pdfBudgetV2: data => api.get('api/budgets-v2/' + data.id + '/pdf', { params: data }),
	sendBudgetWhatsAppV2: data => api.post('api/budgets-v2/' + data.id + '/send-whatsapp', data),
	listBudgetV2Sessions: (budgetId, procedureId) => api.get('api/budgets-v2/' + budgetId + '/procedures/' + procedureId + '/sessions'),
	listBudgetV2PatientBudgetProducts: data => api.get('api/budgets-v2/patient-budget-products', { params: data }),
	deleteBudgetV2SessionProduct: (sessionId, productId) => api.delete(`api/budgets-v2/sessions/${sessionId}/products/${productId}`),
	deleteBudgetV2Session: sessionId => api.delete(`api/budgets-v2/sessions/${sessionId}`),
	getAvailableBudgetV2Sessions: (patientId, appointmentId) =>
		api.get('api/budgets-v2/available-sessions', { params: { patient_id: patientId, ...(appointmentId ? { appointment_id: appointmentId } : {}) } }),
	completeBudgetV2Session: (sessionId, data) => api.put(`api/budgets-v2/sessions/${sessionId}/complete`, data),

	listAccountingDocuments: data => api.get('api/accounting/documents', { params: data }),
	createAccountingDocument: data => api.post('api/accounting/document', data),
	editAccountingDocument: data => api.put('api/accounting/document/' + data.id, data),
	removeAccountingDocument: data => api.delete('api/accounting/document/' + data.id),

	getCEP: data => api.get('api/cep', { params: data }),
	listShopProducts: () => api.get('api/products'),
	listCountries: () => api.get('api/countries'),

	addFinanceAccount: data => api.post('api/settings-finance/account', data),
	listFinanceAccount: data => api.get('api/settings-finance/account', { params: data }),
	updateFinanceAccount: data => api.put('api/settings-finance/account/' + data.id, data),
	removeFinanceAccount: data => api.delete('api/settings-finance/account/' + data.id, data),
	toggleActiveAccount: data => api.put('api/settings-finance/account/status/' + data.id, data),
	findFinanceAccount: data => api.get('/api/settings-finance/find/account', { params: data }),

	addFinanceCategory: data => api.post('api/settings-finance/category', data),
	listFinanceCategory: data => api.get('api/settings-finance/category', { params: data }),
	updateFinanceCategory: data => api.put('api/settings-finance/category/' + data.id, data),
	removeFinanceCategory: data => api.delete('api/settings-finance/category' + data.id, data),
	toggleActiveCategory: data => api.put('api/settings-finance/category/status/' + data.id, data),
	addFinanceCardFee: data => api.post('api/settings-finance/card-fee', data),
	listFinanceCardFee: data => api.get('api/settings-finance/card-fee', { params: data }),
	updateFinanceCardFee: data => api.put('api/settings-finance/card-fee/' + data.id, data),
	removeFinanceCardFee: data => api.delete('api/settings-finance/card-fee' + data.id, data),
	toggleActiveCardFee: data => api.put('api/settings-finance/card-fee/status/' + data.id, data),

	addWhatsappNumber: data => api.post('api/whatsapp-number', data),
	getWhatsappNumber: data => api.get('api/whatsapp-number/info/' + data.id, data),
	listWhatsappNumber: data => api.get('api/whatsapp-numbers', { params: data }),
	updateWhatsappNumber: data => api.put('api/whatsapp-number/' + data.id, data),
	removeWhatsappNumber: data => api.delete('api/whatsapp-number/' + data.id, { params: data }),
	getWhatsappStatus: data => api.get('api/whatsapp-numbers/status', { params: data }),
	changeDefaultWhatsappNumber: data => api.put('api/whatsapp-number/change-default/' + data.id, data),
	getWhatsappPairCode: data => api.post('api/whatsapp-number/' + data.id + '/pair-code'),

	getInvoiceConfiguration: params => api.get('api/invoice-configuration', { params }),
	saveInvoiceConfiguration: data => api.post('api/invoice-configuration', data),
	updateInvoiceConfiguration: data => api.put('api/invoice-configuration', data),
	getMunicipalities: params => api.get('api/invoice-configuration/municipalities', { params }),
	getMunicipalityInfo: params => api.get('api/invoice-configuration/municipality-info', { params }),
	getCnpj: params => api.get('api/invoice-configuration/cnpj', { params }),
	getCnae: params => api.get('api/invoice-configuration/cnae', { params }),
	searchCep: params => api.get('api/invoice-configuration/cep', { params }),
	getServiceItems: params => api.get('api/invoice-configuration/service-items', { params }),
	getMunicipalTaxCodes: params => api.get('api/invoice-configuration/municipal-tax-codes', { params }),

	getInvoiceTaxDefaults: () => api.get('api/invoice-tax-defaults'),
	createInvoiceTaxDefaults: data => api.post('api/invoice-tax-defaults', data),
	updateInvoiceTaxDefaults: data => api.put('api/invoice-tax-defaults', data),
	deleteInvoiceTaxDefaults: () => api.delete('api/invoice-tax-defaults'),
	/** Confere a configuração da NFS-e contra o guia da prefeitura; só orienta, não grava nada. */
	checkInvoiceSetup: body => api.post('api/invoice-setup-assistant/check', body),

	createServiceInvoice: data => api.post('api/service-invoice', data),
	// O cancelamento leva o motivo no corpo: a Focus exige justificativa de 15 a 255 caracteres.
	cancelServiceInvoice: (reference, data) => api.delete(`api/service-invoice/${reference}`, { data }),
	getServiceInvoice: reference => api.get(`api/service-invoice/${reference}`),
	getServiceInvoiceSentPayload: reference => api.get(`api/service-invoice/${reference}/sent-payload`),
	listServiceInvoices: params => api.get('api/service-invoice', { params }),
	sendEmailInvoice: reference => api.post(`api/service-invoice/${reference}/send_email`),
	explainServiceInvoiceError: reference => api.get(`api/service-invoice/${reference}/error-explanation`),
	explainServiceInvoiceRejection: data => api.post('api/service-invoice/rejection-explanation', data),

	addCampaign: data => api.post('api/automation/campaign', data),
	getCampaign: data => api.get('api/automation/campaign/' + data.id, data),
	playCampaign: data => api.put('api/automation/campaign/play/' + data.id, data),
	pauseCampaign: data => api.put('api/automation/campaign/pause/' + data.id, data),
	listCampaigns: data => api.get('api/automation/campaigns', { params: data }),
	removeCampaign: data => api.delete('api/automation/campaign/' + data.id, data),
	getTotalCampaign: data => api.get('api/automation/campaign-audience', { params: data }), // Para contagem por filtros
	getTotalCampaignFromExcel: data => api.post('api/automation/campaign-audience-excel', data), // Para contagem por Excel
	addCampaignFile: data => api.post('api/automation/campaign/file', data),
	downloadCampaignPatients: data =>
		api.get('api/automation/campaign/' + data.campaignId + '/patients', {
			responseType: 'blob',
			headers: {
				Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			}
		}),

	addProduct: data => api.post('api/inventory/product', data),
	updateProduct: data => api.put('api/inventory/product/' + data.id, data),
	listProducts: data => api.get('api/inventory/products', { params: data }),
	addProductFile: data => api.post('api/inventory/product/file', data),
	toggleActiveProduct: data => api.put('api/inventory/product/status/' + data.id, data),
	searchProduct: data => api.get('api/inventory/product/search', { params: data }),
	pdfProducts: data => api.get('api/inventory/products/pdf', { params: data }),
	xlsProducts: data => api.get('api/inventory/products/xls', { params: data }),

	addMovement: data => api.post('api/inventory/movement', data),
	updateMovement: data => api.put('api/inventory/movement/' + data.id, data),
	listMovements: data => api.get('api/inventory/movements', { params: data }),
	removeMovement: data => api.delete('api/inventory/movement/' + data.id, data),
	getMovementBatches: data => api.get('api/inventory/movements/batches', { params: data }),
	getProductCost: data => api.get('api/inventory/movements/cost', { params: data }),
	listMovementReport: data => api.get('api/inventory/movements/report', { params: data }),
	pdfMovementReport: data => api.get('api/inventory/movements/report/pdf', { params: data }),
	xlsMovementReport: data => api.get('api/inventory/movements/report/xls', { params: data }),

	addOrder: data => api.post('api/inventory/order', data),
	listOrders: data => api.get('api/inventory/orders', { params: data }),
	getOrder: data => api.get('api/inventory/order/' + data.id, data),
	removeOrder: data => api.delete('api/inventory/order/' + data.id, data),
	addOrderFile: data => api.post('api/inventory/order/file', data),
	pdfOrders: data => api.get('api/inventory/orders/pdf', { params: data }),
	xlsOrders: data => api.get('api/inventory/orders/xls', { params: data }),

	addSale: data => api.post('api/inventory/sale', data),
	listSales: data => api.get('api/inventory/sales', { params: data }),
	getSale: data => api.get('api/inventory/sale/' + data.id, data),
	removeSale: data => api.delete('api/inventory/sale/' + data.id, data),
	addSaleFile: data => api.post('api/inventory/sale/file', data),
	pdfSales: data => api.get('api/inventory/sales/pdf', { params: data }),
	xlsSales: data => api.get('api/inventory/sales/xls', { params: data }),

	enabledFollowPay: () => api.get('api/follow-pay/enabled'),
	getFollowPayRequiredDocuments: () => api.get('api/follow-pay/account/documents'),
	sendFollowPayDocuments: data => api.post('api/follow-pay/account/documents', data),
	getFollowPayAccount: () => api.get('api/follow-pay/account'),
	createFollowPayAccountCnpj: data => api.post('api/follow-pay/account', data),
	getFollowPayBalance: () => api.get('api/follow-pay/balance'),
	getFollowPayMovements: params => api.get('/api/follow-pay/movement', { params }),
	sendFollowPayPix: data => api.post('/api/follow-pay/pix', data),

	listLeads: data => api.get('api/crm/leads', { params: data }),
	getLead: data => api.get('api/crm/lead/' + data.id),
	addLead: data => api.post('api/crm/lead', data),
	updateLead: data => api.put('api/crm/lead/' + data.id, data),
	removeLead: data => api.delete('api/crm/lead/' + data.id, data),
	getLeadHistory: data => api.get(`/api/crm/lead/${data.lead_id}/history`),
	updateLeadStatus: (leadId, status) => api.put(`/api/crm/leads/${leadId}/status`, { status }),
	searchLead: data => api.get('/api/lead/search', { params: data }),
	filterLeads: data => api.get('/api/lead/filter', { params: data }),
	importLeads: data => api.post('/api/lead/import', data),
	downloadLeadTemplate: data => api.get('/api/lead/template', { params: data }),

	listFunnels: data => api.get('api/funnel-config/funnels', { params: data }),
	getFunnel: data => api.get('api/funnel-config/funnels/' + data.id, data),
	addFunnel: data => api.post('api/funnel-config/funnels', data),
	updateFunnel: (id, data) => api.put('api/funnel-config/funnels/' + id, data),
	removeFunnel: id => api.delete('api/funnel-config/funnels/' + id),
	listFunnelStages: data => api.get('api/funnel-config/stages', { params: data }),
	getFunnelStage: data => api.get('api/funnel-config/stages/' + data.id, data),
	addFunnelStage: data => api.post('api/funnel-config/stages', data),
	updateFunnelStage: (id, data) => api.put('api/funnel-config/stages/' + id, data),
	removeFunnelStage: id => api.delete('api/funnel-config/stages/' + id),
	getFunnelColumnStats: () => api.get('api/funnel-config/column-stats'),

	listFunnelStageTasks: data => api.get('api/funnel-stage-tasks', { params: data }),
	addFunnelStageTask: data => api.post('api/funnel-stage-tasks', data),
	updateFunnelStageTask: data => api.put('api/funnel-stage-tasks/' + data.id, data),
	removeFunnelStageTask: data => api.delete('api/funnel-stage-tasks/' + data.id, data),

	listTags: data => api.get('api/crm/tags', { params: data }),
	addTag: data => api.post('api/crm/tag', data),
	updateTag: data => api.put('api/crm/tag/' + data.id, data),
	removeTag: data => api.delete('api/crm/tag/' + data.id),
	getLeadsByTag: tagId => api.get(`api/crm/tag/${tagId}/leads`),
	addTagToLead: (leadId, tagId) => api.post(`api/crm/lead/${leadId}/tag/${tagId}`),
	removeTagFromLead: (leadId, tagId) => api.delete(`api/crm/lead/${leadId}/tag/${tagId}`),

	listLeadTasks: data => api.get('api/crm/lead-tasks', { params: data }),
	addLeadTask: data => api.post('api/crm/lead-task', data),
	updateLeadTask: data => api.put('api/crm/lead-task/' + data.id, data),
	removeLeadTask: data => api.delete('api/crm/lead-task/' + data.id, data),
	listTaskByFilter: data => api.get('api/crm/lead-tasks/filtered', { params: data }),

	createTelehealthSession: data => api.post(`api/telehealth/sessions/`, data),
	getTelehealthSession: data => api.get(`api/telehealth/sessions/${data.sessionId}`, { params: data.access_token ? { access_token: data.access_token } : undefined }),
	getTelehealthToken: data => api.get(`api/telehealth/sessions/${data.sessionId}/token`, { params: { type: data.type, access_token: data.access_token } }),
	startTelehealthSession: data => api.put(`api/telehealth/sessions/${data.sessionId}/start`, data),
	finishTelehealthSession: data => api.put(`api/telehealth/sessions/${data.sessionId}/finish`, data),
	getTelehealthLinks: data => api.get(`api/telehealth/sessions/${data.sessionId}/link`),
	openTelehealthRoom: data => api.put(`api/telehealth/sessions/${data.sessionId}/open-room`),
	getActiveTelehealthSessionForPatient: data => api.get(`api/telehealth/patients/${data.patientId}/active-session`),
	recordTelehealthConsent: data => api.put(`api/telehealth/sessions/${data.sessionId}/consent`, {}, { params: { access_token: data.access_token } }),
	getTelehealthSessionStatus: data => api.get(`api/telehealth/sessions/${data.sessionId}/status`, { params: { access_token: data.access_token } }),
	checkActiveTelehealthParticipant: data => api.get(`api/telehealth/sessions/${data.sessionId}/active-participant`, { params: { access_token: data.access_token } }),
	disconnectOtherTelehealthDevice: data => api.put(`api/telehealth/sessions/${data.sessionId}/disconnect-other-device`, {}, { params: { access_token: data.access_token } }),
	sendPatientLinkToWhatsApp: data => api.post(`/api/telehealth/sessions/${data.sessionId}/patient/send-to-whatsapp`),
	generateTelehealthSessionReport: data => api.get(`/api/telehealth/sessions/${data.sessionId}/report`),
	getTelehealthChatMessages: data => api.get(`api/telehealth/sessions/${data.sessionId}/chat-messages`, { params: data.accessToken ? { access_token: data.accessToken } : {} }),
	uploadTelehealthChatAttachment: data =>
		api.post(`api/telehealth/sessions/${data.sessionId}/chat-messages/upload`, data.formData, { params: data.accessToken ? { access_token: data.accessToken } : {} }),
	getTelehealthSessionDocuments: data =>
		api.get(`api/telehealth/sessions/${data.sessionId}/documents`, {
			params: {
				...(data.accessToken ? { access_token: data.accessToken } : {}),
				...(data.last4Digits ? { last_4_digits: data.last4Digits } : {})
			}
		}),
	shareTelehealthSessionDocuments: data => api.put(`api/telehealth/sessions/${data.sessionId}/share-documents`, data),

	getPlanFeatures: () => api.get('api/plan/features'),
	listPackages: data => api.get('api/packages', { params: data }),
	getPackage: data => api.get('api/packages/' + data.id),
	addPackage: data => api.post('api/packages', data),
	updatePackage: data => api.put('api/packages/' + data.id, data),
	togglePackageStatus: id => api.put('api/packages/' + id + '/status'),
	removePackageItem: data => api.delete('api/packages/' + data.id + '/items/' + data.itemId),

	listPatientPackageItems: data => api.get('api/patient-packages/' + data.patientId + '/items'),
	listPatientPackages: data => api.get('api/patient-packages/' + data.patientId + ''),
	getPatientPackageById: packageId => api.get(`api/patient-packages/${packageId}`),
	createPatientPackage: data => api.post('api/patient-packages/' + data.patientId + '', data),
	//updatePatientPackage: (packageId, data) => api.put(`api/patient-packages/${packageId}`, data),
	usePatientPackageItem: data => api.post('api/patient-packages/' + data.patientId + '/items/' + data.itemId + '/use', data),
	convertPatientPackageItemToCredit: data => api.post('api/patient-packages/' + data.patientId + '/items/' + data.itemId + '/credit', data),
	verifyPackagePaymentStatus: packageId => api.get('api/patient-packages/' + packageId + '/payment-status'),
	getPatientPackagePdf: packageId => api.get(`api/patient-packages/${packageId}/pdf`),
	deletePatientPackage: packageId => api.delete('api/patient-packages/' + packageId),

	getEnvs: () => api.get('api/front-envs'),

	getOriginalUrl: shortCode => api.get(`api/short/${shortCode}/original`),

	listNfes: data => api.get('api/nfes', { params: data }),
	getNfeXml: data => api.get('api/nfe/xml/' + data.id, { params: data, responseType: 'blob' }),
	getNfePdf: data => api.get('api/nfe/pdf/' + data.id, { params: data }),
	pdfNfes: data => api.get('api/nfes/pdf', { params: data }),
	xlsNfes: data => api.get('api/nfes/xls', { params: data }),

	getAgentConfiguration: () => api.get('api/agent/configuration'),
	saveAgentConfiguration: data => api.post('api/agent/configuration', data),
	listAgentFaqs: params => api.get('api/agent/faq', { params }),
	addAgentFaq: data => api.post('api/agent/faq', data),
	removeAgentFaq: data => api.delete('api/agent/faq/' + data.id, { data }),
	listAgentFiles: params => api.get('api/agent/files', { params }),
	uploadAgentFile: data => api.post('api/agent/files', data),
	removeAgentFile: id => api.delete('api/agent/files/' + id),
	listAgentIgnoredContacts: params => api.get('api/agent/ignored-contacts', { params }),
	addAgentIgnoredContact: data => api.post('api/agent/ignored-contacts', data),
	removeAgentIgnoredContact: data => api.delete('api/agent/ignored-contacts/' + data.id, { data }),
	getAgentSelectedProfessionals: () => api.get('api/agent/professionals/selected'),
	setAgentSelectedProfessionals: data => api.post('api/agent/professionals/selected', data),
	getAgentSelectedProcedures: () => api.get('api/agent/procedures/selected'),
	setAgentSelectedProcedures: data => api.post('api/agent/procedures/selected', data),
	wabaOnboarding: data => api.post('api/waba/onboarding', data),
	logWabaOnboardingEvent: data => api.post('api/waba/onboarding/events', data),
	wabaCreateUnsubmittedTemplates: () => api.post('api/waba/create-unsubmitted-templates'),
	wabaValidateTemplate: data => api.post('api/waba/validate-template', data),

	getHealthInsurances: filters => api.get('api/health-insurances', { params: filters }),

	getHealthInsurance: (id, includeAccount) =>
		api.get(`api/health-insurances/${id}`, {
			params: includeAccount ? { include_account: true } : {}
		}),

	createHealthInsurance: data => api.post('api/health-insurances', data),

	updateHealthInsurance: (id, data) => api.put(`api/health-insurances/${id}`, data),

	deleteHealthInsurance: id => api.delete(`api/health-insurances/${id}`),

	listHealthInsuranceReport: data => api.get('api/reports/health-insurance', { params: data }),
	printHealthInsuranceReport: data => api.get('api/reports/health-insurance/print', { params: data }),
	updateHealthInsuranceReportDiscount: data => api.patch('api/reports/health-insurance/procedure-discount', data),

	// Health Insurance Plans
	listPlans: params => api.get('api/health-insurance-plans', { params }),
	getPlan: id => api.get(`api/health-insurance-plans/${id}`),
	createPlan: data => api.post('api/health-insurance-plans', data),
	updatePlan: (id, data) => api.put(`api/health-insurance-plans/${id}`, data),
	deletePlan: id => api.delete(`api/health-insurance-plans/${id}`),
	getPlanDoctors: id => api.get(`api/health-insurance-plans/${id}/doctors`),
	setPlanDoctors: (id, doctor_ids) => api.post(`api/health-insurance-plans/${id}/doctors`, { doctor_ids }),

	// Hospitals
	listHospitals: data => api.get('api/hospital', { params: data }),
	getHospital: id => api.get(`api/hospital/${id}`),
	createHospital: data => api.post('api/hospital', data),
	updateHospital: (id, data) => api.put(`api/hospital/${id}`, data),
	deleteHospital: id => api.delete(`api/hospital/${id}`),

	// Locations (Localizações)
	listLocations: params => api.get('api/locations', { params }),
	getLocation: id => api.get(`api/locations/${id}`),
	createLocation: data => api.post('api/locations', data),
	updateLocation: (id, data) => api.put(`api/locations/${id}`, data),
	deleteLocation: id => api.delete(`api/locations/${id}`),
	toggleLocationActive: id => api.put(`api/locations/${id}/toggle-active`),
	getLocationUsers: id => api.get(`api/locations/${id}/users`),
	setLocationUsers: (id, user_ids) => api.post(`api/locations/${id}/users`, { user_ids }),
	// Room Select (com médico ou guichê associado, dependendo do tipo de usuário)
	listRoomsForSelect: params => api.get('api/room-select', { params }),
	selectRoom: (id, doctorId) => api.put('api/room-select/select', { room_id: id, doctor_id: doctorId }),

	// Guichês (ticket-windows)
	listTicketWindows: params => api.get('api/ticket-windows', { params }),
	createTicketWindow: data => api.post('api/ticket-windows', data),
	updateTicketWindow: (id, data) => api.put(`api/ticket-windows/${id}`, data),
	deleteTicketWindow: id => api.delete(`api/ticket-windows/${id}`),
	selectTicketWindow: (id, userId) => api.put('api/ticket-window/select', { ticket_window_id: id, user_id: userId }),
	getTicketWindowHistory: (id, params) => api.get(`api/ticket-windows/${id}/history`, { params }),
	callPatientFromTicketWindow: data => api.post('api/ticket-window/call', data),

	// Tipos de senha (ticket-types)
	listTicketTypes: params => api.get('api/ticket-types', { params }),
	createTicketType: data => api.post('api/ticket-types', data),
	updateTicketType: (id, data) => api.put(`api/ticket-types/${id}`, data),
	deleteTicketType: id => api.delete(`api/ticket-types/${id}`),
	// Clinic Environments
	listClinicEnvironments: params => api.get('api/clinic-environments', { params }),
	getClinicEnvironment: id => api.get(`api/clinic-environments/${id}`),
	createClinicEnvironment: data => api.post('api/clinic-environments', data),
	updateClinicEnvironment: (id, data) => api.put(`api/clinic-environments/${id}`, data),
	deleteClinicEnvironment: id => api.delete(`api/clinic-environments/${id}`),
	toggleActiveClinicEnvironment: id => api.patch(`api/clinic-environments/${id}/toggle-active`),
	// Clinic Portal
	getClinicPortal: () => api.get('api/clinic-portal/account'),
	updateClinicPortal: data => api.put('api/clinic-portal/account', data),

	// Receptions (Recepções)
	listReceptions: params => api.get('api/receptions', { params }),
	getReception: id => api.get(`api/receptions/${id}`),
	createReception: data => api.post('api/receptions', data),
	updateReception: (id, data) => api.put(`api/receptions/${id}`, data),
	toggleActiveReception: id => api.patch(`api/receptions/${id}/toggle-active`),
	generateCallerCode: id => api.post(`api/receptions/${id}/generate-caller-code`),

	// Waiting lists (Filas de espera)
	listWaitingLists: params => api.get('api/waiting-list', { params }),
	createWaitingList: data => api.post('api/waiting-list', data),
	updateWaitingList: (id, data) => api.put(`api/waiting-list/${id}`, data),
	deleteWaitingList: id => api.delete(`api/waiting-list/${id}`),
	linkAppointmentWaitingList: (appointmentId, data) => api.patch(`api/appointment/${appointmentId}/waiting-list`, data),

	getCallerPublic: callerCode => api.get(`api/caller-public/${callerCode}`),
	callPatient: data => api.post('api/call-patient', data),

	// Totem de autoatendimento (público)
	getTotemConfig: receptionCode => api.get(`api/totem/${receptionCode}`),
	totemCheckin: data => api.post('api/totem/checkin', data),
	totemLookupPatient: (receptionCode, cpf) => api.get(`api/totem/${receptionCode}/patient`, { params: { cpf } }),

	listPatientInsurances: params => api.get('api/patient-insurances', { params }),
	getPatientInsurance: id => api.get(`api/patient-insurances/${id}`),
	createPatientInsurance: data => api.post('api/patient-insurances', data),
	updatePatientInsurance: (id, data) => api.put(`api/patient-insurances/${id}`, data),
	deletePatientInsurance: id => api.delete(`api/patient-insurances/${id}`),
	getPatientInsurancesByPatient: patientId => api.get(`api/patients/${patientId}/insurances`),
	makePlanDefault: id => api.put(`api/patient-insurances/${id}/make-default`),

	listWhatsappMessages: params => api.get('api/whatsapp-messages', { params }),

	digitalSignatureStatus: () => api.get('/api/digital-signature/status'),
	loginDigitalSignature: data => api.post('/api/digital-signature/login', data),
	digitalSignatureTokenInfo: () => api.get('/api/digital-signature/token-info'),
	disconnectDigitalSignature: provider => api.delete('/api/digital-signature/disconnect', { params: provider ? { provider } : undefined }),
	startVidaasAuthentication: () => api.post('/api/digital-signature/vidaas/authentication'),
	vidaasAuthenticationStatus: state => api.get('/api/digital-signature/vidaas/status', { params: { state } }),

	generatePrenatalShareLink: data => api.post('api/prenatal/share-link', data),
	getPrenatalByShortCode: shortCode => api.get(`api/prenatal-public/${shortCode}`),
	validatePrenatalAccess: (shortCode, data) => api.post(`api/prenatal-public/${shortCode}/validate`, data),
	sharePrenatalViaWhatsapp: data => api.post('api/prenatal/share-whatsapp', data),

	listHealthRecordReportFlags: params => api.get('api/health-record-report-flags', { params }),
	getHealthRecordReportFlag: id => api.get(`api/health-record-report-flags/${id}`),
	createHealthRecordReportFlag: data => api.post('api/health-record-report-flags', data),
	updateHealthRecordReportFlag: (id, data) => api.put(`api/health-record-report-flags/${id}`, data),
	deleteHealthRecordReportFlag: id => api.delete(`api/health-record-report-flags/${id}`),

	getNextAppointments: params => api.get('api/next-appointments', { params }),
	getNextAppointmentsHistory: params => api.get('api/next-appointments-history', { params }),
	getNextAppointmentsOverview: params => api.get('api/next-appointments/overview', { params }),
	getNextAppointmentsUnfinished: params => api.get('api/next-appointments/unfinished', { params }),
	listRecordRequesters: params => api.get('api/requesters', { params }),
	getRecordRequester: id => api.get(`api/requester/${id}`),
	createRecordRequester: data => api.post('api/requester', data),
	updateRecordRequester: (id, data) => api.put(`api/requester/${id}`, data),
	updateStatusAction: (id, data) => api.put(`api/requester-status/${id}`, data),

	listDeliveryReceipts: params => api.get('api/delivery-receipts', { params }),
	getDeliveryReceipt: id => api.get(`api/delivery-receipts/${id}`),
	createDeliveryReceipt: data => api.post('api/delivery-receipts', data),
	updateDeliveryReceipt: (id, data) => api.put(`api/delivery-receipts/${id}`, data),
	deleteDeliveryReceipt: id => api.delete(`api/delivery-receipts/${id}`),
	getDeliveryReceiptProtocolPdf: params => api.get('api/delivery-receipts/protocol', { params }),

	// Health Record Report Review Reasons
	listHealthRecordReportReviewReasons: params => api.get('api/health-record-report-review-reasons', { params }),
	getHealthRecordReportReviewReason: id => api.get(`api/health-record-report-review-reasons/${id}`),
	createHealthRecordReportReviewReason: data => api.post('api/health-record-report-review-reasons', data),
	updateHealthRecordReportReviewReason: (id, data) => api.put(`api/health-record-report-review-reasons/${id}`, data),
	deleteHealthRecordReportReviewReason: id => api.delete(`api/health-record-report-review-reasons/${id}`),

	// PACS
	getPacsImageLink: (patient_id, record_id) => api.get('api/pacs/image-link', { params: { patient_id, record_id } }),

	// Schedule (Central de Agendamentos)
	getAvailableSlots: params => api.get('api/schedule/available-slots', { params }),
	getMonthlyAvailability: params => api.get('api/schedule/monthly-availability', { params }),
	getAgendaStats: params => api.get('api/schedule/agenda-stats', { params }),

	// Medications
	listMedications: params => api.get('api/medications', { params }),
	listMedicationDosages: id => api.get(`api/medications/${id}/dosages`),
	generateMedicationDosageSuggestions: id => api.post(`api/medications/${id}/dosages/suggestions`),
	listMedicationShortcuts: params => api.get('api/medications/shortcuts', { params }),
	deleteMedicationShortcut: (id, mine) => api.delete(`api/medications/shortcuts/${id}`, { params: { mine } }),
	listManipulatedMedications: params => api.get('api/manipulated-medications', { params }),
	getMedicationShortcut: (id, params) => api.get(`api/medications/shortcuts/${id}`, { params }),
	createMedicationShortcut: data => api.post('api/medications/shortcuts', data),
	updateMedicationShortcut: data => api.put(`api/medications/shortcuts/${data.id}`, data),

	// Custom Medications (Receituário - settings)
	listCustomMedications: params => api.get('api/custom-medication', { params }),
	createCustomMedication: data => api.post('api/custom-medication', data),
	updateCustomMedication: data => api.put('api/custom-medication/' + data.id, data),
	deleteCustomMedication: (id, mine) => api.delete('api/custom-medication/' + id, { params: { mine } }),
	listCustomManipulatedMedications: params => api.get('api/custom-manipulated-medication', { params }),
	createCustomManipulatedMedication: data => api.post('api/custom-manipulated-medication', data),
	updateCustomManipulatedMedication: data => api.put('api/custom-manipulated-medication/' + data.id, data),
	deleteCustomManipulatedMedication: (id, mine) => api.delete('api/custom-manipulated-medication/' + id, { params: { mine } }),

	// Health Record Public Validation
	getHealthRecordByValidationCode: code => api.get(`api/health-record/public/validation/${code}`),
	getHealthRecordPublicPdf: (code, last_4_digits) => api.post(`api/health-record/public/validation/${code}/pdf`, { last_4_digits }),

	// API Keys
	listApiKeys: () => api.get('api/api-keys'),
	createApiKey: data => api.post('api/api-keys', data),
	updateApiKey: (id, data) => api.put(`api/api-keys/${id}`, data),
	deleteApiKey: id => api.delete(`api/api-keys/${id}`),
	getKommoConfig: () => api.get('api/kommo/config'),
	upsertKommoConfig: data => api.put('api/kommo/config', data),

	startTranscriptionSession: data => api.post('/api/transcription/start-session', data),
	getTranscriptionStreamCredentials: data => api.post(`/api/transcription/session/${data.sessionId}/stream-credentials`, { speaker: data.speaker ?? null }),
	getTranscriptionSegments: data => api.get(`/api/transcription/session/${data.sessionId}/segments`),
	finishTranscriptionSession: data => api.put(`/api/transcription/session/${data.sessionId}/finish`),
	getUnfinishedTranscriptionSession: () => api.get('/api/transcription/sessions/unfinished'),
	listTranscriptionSessions: data => api.get(`/api/transcription/patient/${data.patientId}/sessions`, { params: data }),
	linkTranscriptionAnamnese: data => api.put(`/api/transcription/session/${data.sessionId}/anamnese`, { healthRecordId: data.healthRecordId }),
	getTranscriptionByAnamnese: data => api.get(`/api/transcription/anamnese/${data.healthRecordId}/segments`),

	createConsultationGeneration: data => api.post('/api/consultation-documents/generations', data),
	getConsultationGeneration: data => api.get(`/api/consultation-documents/generations/${data.id}`),
	findConsultationGeneration: data => api.get('/api/consultation-documents/generations', { params: data }),
	recordConsultationDecisions: data => api.post(`/api/consultation-documents/generations/${data.id}/decisions`, { records: data.records, unsaved_subtypes: data.unsaved_subtypes ?? [] }),
	listTranscriptionSessionDocuments: data => api.get(`/api/consultation-documents/sessions/${data.sessionId}/documents`),

	post: (url, data) => api.post(url, data)
};

export default apis;
